import { cloneElement, isValidElement, useId, useState, type ReactElement, type ReactNode } from "react";
import { Check, Copy, Loader } from "lucide-react";
import { toast } from "sonner";

import { ApiError } from "@/api/fetcher";
import { Button } from "@/components/ui/button";
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import { cn } from "@/lib/utils";

export function PageHeader({ title, description, actions }: { title: string; description?: ReactNode; actions?: ReactNode }) {
  return (
    <div className="flex flex-wrap items-end justify-between gap-3 border-b px-6 py-4">
      <div>
        <h1 className="text-lg font-semibold tracking-tight">{title}</h1>
        {description && <p className="mt-0.5 text-sm text-muted-foreground">{description}</p>}
      </div>
      {actions && <div className="flex items-center gap-2">{actions}</div>}
    </div>
  );
}

export function EmptyState({ title, hint, action }: { title: string; hint?: ReactNode; action?: ReactNode }) {
  return (
    <div className="flex flex-col items-center justify-center gap-2 rounded-lg border border-dashed px-6 py-12 text-center">
      <p className="text-sm font-medium">{title}</p>
      {hint && <p className="max-w-md text-sm text-muted-foreground">{hint}</p>}
      {action}
    </div>
  );
}

export function Loading({ label = "Loading…" }: { label?: string }) {
  return (
    <div className="flex items-center gap-2 px-6 py-8 text-sm text-muted-foreground">
      <Loader className="size-4 animate-spin" /> {label}
    </div>
  );
}

export function errorText(err: unknown): string {
  if (err instanceof ApiError) return err.message;
  if (err instanceof Error) return err.message;
  return "Something went wrong";
}

export function ErrorNote({ error, className }: { error: unknown; className?: string }) {
  if (!error) return null;
  return <p className={cn("rounded-md border border-destructive/40 bg-destructive/10 px-3 py-2 text-sm text-destructive", className)}>{errorText(error)}</p>;
}

export function CopyButton({ text, label = "Copy" }: { text: string; label?: string }) {
  const [done, setDone] = useState(false);
  return (
    <Button
      type="button"
      size="sm"
      variant="outline"
      onClick={async () => {
        try {
          await navigator.clipboard.writeText(text);
          setDone(true);
          window.setTimeout(() => setDone(false), 1500);
        } catch {
          // Insecure context or denied permission: say so, so nobody pastes
          // whatever was on the clipboard before.
          toast.error("Could not copy to the clipboard", { description: "Select the text and copy it by hand." });
        }
      }}
    >
      {done ? <Check className="size-3.5" /> : <Copy className="size-3.5" />}
      {done ? "Copied" : label}
    </Button>
  );
}

export function CodeBlock({ code }: { code: string }) {
  return (
    <div className="relative">
      <pre className="overflow-x-auto rounded-md border bg-muted/60 p-3 pr-24 font-mono text-xs leading-relaxed whitespace-pre-wrap break-all">{code}</pre>
      <div className="absolute top-2 right-2">
        <CopyButton text={code} />
      </div>
    </div>
  );
}

export function ConfirmDialog({
  open, onOpenChange, title, description, confirmLabel = "Confirm", destructive, busy, onConfirm, error,
}: {
  open: boolean; onOpenChange: (v: boolean) => void; title: string; description?: ReactNode; confirmLabel?: string;
  destructive?: boolean; busy?: boolean; onConfirm: () => void; error?: unknown;
}) {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{title}</DialogTitle>
          {description && <DialogDescription>{description}</DialogDescription>}
        </DialogHeader>
        <ErrorNote error={error} />
        <DialogFooter>
          <Button variant="ghost" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button variant={destructive ? "destructive" : "default"} disabled={busy} onClick={onConfirm}>
            {busy && <Loader className="size-3.5 animate-spin" />} {confirmLabel}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

/** A labelled control. The hint sits outside the <label> so it describes the
 * control instead of becoming part of its accessible name. */
export function Field({ label, hint, children, className }: { label: string; hint?: ReactNode; children: ReactNode; className?: string }) {
  const hintId = useId();
  const control = hint && isValidElement(children) ? cloneElement(children as ReactElement<{ "aria-describedby"?: string }>, { "aria-describedby": hintId }) : children;
  return (
    // min-w-0 and minmax(0, 1fr) columns: a long value truncates instead of
    // widening the field. content-start: a hint under one field doesn't push
    // its neighbours' inputs down.
    <div className={cn("grid min-w-0 grid-cols-1 content-start gap-1.5 text-sm", className)}>
      <label className="grid grid-cols-1 gap-1.5">
        <span className="font-medium">{label}</span>
        {control}
      </label>
      {hint && <span id={hintId} className="text-xs text-muted-foreground">{hint}</span>}
    </div>
  );
}
